// Copyright (c) 2024 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { BffWebSocketClient, LogEmitBatchCommand, LogEmitCommand } from './bffWebSocket';
import { getSharedBffWebSocket } from './bffWebSocket';
import { getClientEnvironmentDetails } from './clientEnvironment';
import { bufferDebugLog, drainDebugLogs, type BufferedLogEntry } from './debugLogBuffer';
import { useDebugState } from '@/stores/debugState';

type LogLevel = NonNullable<LogEmitCommand['level']>;

/** What made the buffered debug lines leave the phone. */
export type DebugLogFlushReason = 'bug.report' | 'error' | 'debug.enabled' | 'round.summary';

type LogSender = Pick<BffWebSocketClient, 'sendLog' | 'sendLogBatch'>;

type LogBatch = Omit<LogEmitBatchCommand, 'type' | 'service' | 'timestamp'>;

/** Levels as a batch frame indexes them. The BFF reads the table from the frame, never from here. */
const BATCH_LEVELS: LogLevel[] = ['debug', 'info', 'warn', 'error'];

/** Lines per frame: a thousand is about thirty kilobytes, so a whole round is a few frames. */
const BATCH_MAX_LINES = 1000;

/**
 * Debug lines stream live only while the debug flag (?debug=true) is on. Otherwise they stay
 * in the in-memory ring buffer and leave on a trigger — see debugLogBuffer.ts.
 */
const debugLoggingLive = (): boolean => useDebugState.getState().enabled;

const environmentAttributes = (): Record<string, unknown> => ({
  environment: process.env.EXPO_PUBLIC_APP_ENV ?? process.env.NODE_ENV ?? 'unknown',
  ...getClientEnvironmentDetails(),
});

/**
 * One frame for a run of lines: each distinct message and attribute set is sent once, and a line
 * is four numbers. The buffer hands identical attribute sets back as one object, so the context
 * table is built by identity. A 1,656-line round measured 1.5 MB as one frame per line and
 * 105 KB this way.
 */
const buildLogBatch = (entries: BufferedLogEntry[], attributes: Record<string, unknown>): LogBatch => {
  const messages: string[] = [];
  const messageIndex = new Map<string, number>();
  const contexts: Record<string, unknown>[] = [];
  const contextIndex = new Map<Record<string, unknown>, number>();
  const lines: LogBatch['lines'] = entries.map((entry) => {
    let message = messageIndex.get(entry.message);
    if (message === undefined) {
      message = messages.push(entry.message) - 1;
      messageIndex.set(entry.message, message);
    }
    let context = -1;
    if (entry.attributes) {
      const known = contextIndex.get(entry.attributes);
      if (known === undefined) {
        context = contexts.push(entry.attributes) - 1;
        contextIndex.set(entry.attributes, context);
      } else {
        context = known;
      }
    }
    return [entry.at, Math.max(0, BATCH_LEVELS.indexOf(entry.level)), message, context];
  });
  return { attributes, levels: BATCH_LEVELS, messages, contexts, lines };
};

/**
 * Ships everything in the ring buffer over `client`, oldest first, in batch frames of at most
 * {@link BATCH_MAX_LINES} lines. Every line keeps its own time and is marked `deferred` with the
 * reason it is leaving now. Returns how many lines went.
 */
export const flushDebugLogs = (
  client: LogSender,
  reason: DebugLogFlushReason,
  extra?: Record<string, unknown>
): number => {
  const { entries, dropped } = drainDebugLogs();
  if (entries.length === 0) return 0;
  // Read once per flush: the environment is the same for every line, which is also why a
  // buffered line does not carry it until now.
  const shared = { ...environmentAttributes(), deferred: true, flushReason: reason, ...extra };
  for (let start = 0; start < entries.length; start += BATCH_MAX_LINES) {
    // Only the first frame says how many older lines the buffer had already let go.
    const attributes = start === 0 && dropped > 0 ? { ...shared, droppedBeforeFlush: dropped } : shared;
    client.sendLogBatch(buildLogBatch(entries.slice(start, start + BATCH_MAX_LINES), attributes));
  }
  return entries.length;
};

export const createAppLogger = (
  baseHttpUrl: string,
  token?: string | null,
  defaultAttributes?: Record<string, unknown>,
  socketClient?: BffWebSocketClient | null
) => {
  const client = socketClient ?? getSharedBffWebSocket(baseHttpUrl);
  client.setAuthToken(token ?? null);

  return (message: string, attributes?: Record<string, unknown>, level: LogLevel = 'info') => {
    try {
      if (level === 'debug' && !debugLoggingLive()) {
        // No environment lookup and no socket write per line: in a round the hole screen writes
        // one or two of these per inbound message, and sending each one is what kept the radio
        // awake all round.
        bufferDebugLog({
          message,
          level,
          attributes: { ...defaultAttributes, ...attributes },
          at: Date.now(),
        });
        return;
      }
      client.setAuthToken(token ?? null);
      // An error is the moment the lines before it become worth having. A debug line going live
      // (the flag just came on) ships the backlog first, so the stream reads in order.
      if (level === 'error') {
        flushDebugLogs(client, 'error');
      } else if (level === 'debug') {
        flushDebugLogs(client, 'debug.enabled');
      }
      client.sendLog({
        message,
        level,
        attributes: { ...defaultAttributes, ...environmentAttributes(), ...attributes },
      });
    } catch (err) {
      console.log('[appLogger][fallback]', message, attributes, err);
    }
  };
};
